import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState } from '@/shared/ui';

import {
  useCatalogFeedColumnMapping,
  useCatalogFeedFile,
  useCreateCatalogFeedColumnMapping,
  useDownloadTemplate,
  useExportAllCatalogFeedProducts,
  useExportCatalogFeedProduct,
  useGoogleSheetSettings,
  useImportFile,
  useImportFromGoogleSheet,
  useReprocessCatalogFeedFile,
  useUpdateCatalogFeedColumnMapping,
  useUpdateGoogleSheetSettings,
  useUploadCatalogFeedFile,
} from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import type { CatalogFeedColumnMappingRequest, CatalogFeedFileStatus, ImportRun } from '../../model/types';

function runColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'RUNNING') return 'warning';
  return 'default';
}

function RunSummary({ run }: { run: ImportRun }) {
  const { t } = useTranslation('catalog');
  return (
    <Alert severity={run.status === 'FAILED' ? 'error' : 'success'} icon={false} sx={{ mt: 1 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color={runColor(run.status)} label={run.status} />
          <Chip size="small" variant="outlined" label={run.source} />
          {run.dryRun ? (
            <Chip size="small" variant="outlined" color="info" label={t('importPanel.lastRun.dryRunChip')} />
          ) : null}
        </Stack>
        <Typography variant="body2">
          {t('importPanel.lastRun.counts', {
            categories: formatNumber(run.categoriesCount),
            products: formatNumber(run.productsCount),
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {run.finishedAt
            ? t('importPanel.lastRun.startedAndFinished', {
                startDate: formatDateTime(run.startedAt),
                finishDate: formatDateTime(run.finishedAt),
              })
            : t('importPanel.lastRun.startedOnly', { startDate: formatDateTime(run.startedAt) })}
        </Typography>
        {run.error ? (
          <Typography variant="body2" color="error">
            {run.error}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}

function feedFileStatusColor(status: CatalogFeedFileStatus): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED' || status === 'VALIDATION_ERROR') return 'error';
  if (status === 'PROCESSING' || status === 'UPLOADED') return 'warning';
  return 'default';
}

function resolveSpreadsheetUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(trimmed)}`;
}

/**
 * Bulk catalog import form — Excel template, file upload, and Google-Sheet sync.
 *
 * Self-contained (state, permission gate, hooks and feedback) so it can render
 * both as a standalone page (`ImportPage`) and inside a slideout (`ImportFlyout`)
 * on the Products workspace. It owns no layout chrome (no page/drawer wrapper).
 */
export function ImportPanel() {
  const { t } = useTranslation('catalog');
  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [lastRun, setLastRun] = useState<ImportRun | null>(null);

  const feedFileInputRef = useRef<HTMLInputElement | null>(null);
  const [feedFile, setFeedFile] = useState<File | null>(null);
  const [feedFileId, setFeedFileId] = useState<string | null>(null);
  const [mappingSheetName, setMappingSheetName] = useState('');
  const [mappingActive, setMappingActive] = useState(true);
  const [mappingRows, setMappingRows] = useState<{ header: string; field: string }[]>([
    { header: '', field: '' },
  ]);
  const [exportArticle, setExportArticle] = useState('');

  const download = useDownloadTemplate();
  const importFile = useImportFile();
  const importSheet = useImportFromGoogleSheet();
  const sheetSettings = useGoogleSheetSettings({ enabled: canView });
  const saveSheet = useUpdateGoogleSheetSettings();

  const uploadFeed = useUploadCatalogFeedFile();
  const feedFileStatus = useCatalogFeedFile(feedFileId ?? undefined, {
    enabled: canView && feedFileId !== null,
  });
  const reprocessFeed = useReprocessCatalogFeedFile();
  const exportProduct = useExportCatalogFeedProduct();
  const exportAll = useExportAllCatalogFeedProducts();
  const mapping = useCatalogFeedColumnMapping({ enabled: canView });
  const createMapping = useCreateCatalogFeedColumnMapping();
  const updateMapping = useUpdateCatalogFeedColumnMapping();

  useEffect(() => {
    if (sheetSettings.data?.spreadsheetId) setSpreadsheetId(sheetSettings.data.spreadsheetId);
  }, [sheetSettings.data]);

  useEffect(() => {
    const m = mapping.data;
    if (!m) return;
    setMappingSheetName(m.sheetName);
    setMappingActive(m.active);
    const rows = Object.entries(m.columnMapping).map(([header, field]) => ({ header, field }));
    setMappingRows(rows.length > 0 ? rows : [{ header: '', field: '' }]);
  }, [mapping.data]);

  if (!canView) {
    return (
      <ErrorState
        title={t('importPanel.accessRestrictedTitle')}
        message={t('importPanel.accessRestrictedMessage')}
      />
    );
  }

  const mappingNotConfigured = mapping.isError && mapping.error?.status === 404;

  const setMappingRow = (index: number, patch: Partial<{ header: string; field: string }>) =>
    setMappingRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const addMappingRow = () => setMappingRows((rows) => [...rows, { header: '', field: '' }]);
  const removeMappingRow = (index: number) =>
    setMappingRows((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== index) : rows));

  const saveMapping = () => {
    const columnMapping = Object.fromEntries(
      mappingRows
        .map((r) => [r.header.trim(), r.field.trim()] as const)
        .filter(([header, field]) => header !== '' && field !== ''),
    );
    const body: CatalogFeedColumnMappingRequest = {
      sheetName: mappingSheetName.trim(),
      columnMapping,
      active: mappingActive,
    };
    const mutation = mappingNotConfigured ? createMapping : updateMapping;
    mutation.mutate(body, {
      onSuccess: () => success(t('importPanel.feed.mapping.saved')),
      onError: (error) => notifyError(error.message),
    });
  };

  const runFeedUpload = () => {
    if (!feedFile) return;
    uploadFeed.mutate(feedFile, {
      onSuccess: (result) => {
        setFeedFileId(result.fileId);
        success(t('importPanel.feed.uploadAccepted'));
      },
      onError: (error) => notifyError(error.message),
    });
  };

  const runReprocess = () => {
    if (!feedFileId) return;
    reprocessFeed.mutate(feedFileId, {
      onSuccess: () => success(t('importPanel.feed.reprocessAccepted')),
      onError: (error) => notifyError(error.message),
    });
  };

  const runExportProduct = () => {
    if (exportArticle.trim() === '') return;
    exportProduct.mutate(exportArticle.trim(), {
      onSuccess: () => success(t('importPanel.feed.export.productAccepted')),
      onError: (error) => notifyError(error.message),
    });
  };

  const runExportAll = () => {
    exportAll.mutate(undefined, {
      onSuccess: () => success(t('importPanel.feed.export.allAccepted')),
      onError: (error) => notifyError(error.message),
    });
  };

  const runFileImport = () => {
    if (!file) return;
    importFile.mutate(
      { file, dryRun },
      {
        onSuccess: (run) => {
          setLastRun(run);
          success(dryRun ? t('importPanel.fileChecked') : t('importPanel.importCompleted'));
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  const runSheetImport = () => {
    importSheet.mutate(
      { dryRun },
      {
        onSuccess: (run) => {
          setLastRun(run);
          success(dryRun ? t('importPanel.sheetChecked') : t('importPanel.importCompleted'));
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  return (
    <>
      <Stack spacing={2.5}>
        <FormControlLabel
          control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
          label={t('importPanel.dryRunLabel')}
        />

        {/* Excel file import */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {t('importPanel.excel.heading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPanel.excel.description')}
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={download.isPending ? <CircularProgress size={16} /> : <DownloadRoundedIcon />}
                disabled={download.isPending}
                onClick={() =>
                  download.mutate(undefined, {
                    onError: (error) => notifyError(error.message),
                  })
                }
                >
                  {t('importPanel.excel.downloadTemplate')}
                </Button>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<TableChartRoundedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canManage}
                >
                  {t('importPanel.excel.chooseFile')}
                </Button>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                  {file?.name ?? t('importPanel.excel.noFileChosen')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={importFile.isPending ? <CircularProgress size={16} color="inherit" /> : <CloudUploadRoundedIcon />}
                  disabled={!canManage || !file || importFile.isPending}
                  onClick={runFileImport}
                >
                  {dryRun ? t('importPanel.excel.check') : t('common:action.import')}
                </Button>
              </Stack>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </CardContent>
        </Card>

        {/* Google Sheet */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {t('importPanel.googleSheet.heading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPanel.googleSheet.description')}
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  label={t('importPanel.googleSheet.linkLabel')}
                  value={spreadsheetId}
                  onChange={(e) => setSpreadsheetId(e.target.value)}
                  disabled={!canManage}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  startIcon={saveSheet.isPending ? <CircularProgress size={16} /> : <SaveRoundedIcon />}
                  disabled={!canManage || spreadsheetId.trim() === '' || saveSheet.isPending}
                  onClick={() =>
                    saveSheet.mutate(
                      { spreadsheetId: spreadsheetId.trim() },
                      {
                        onSuccess: () => success(t('importPanel.googleSheet.settingsSaved')),
                        onError: (error) => notifyError(error.message),
                      },
                    )
                  }
                  >
                  {t('common:action.save')}
                </Button>
              </Stack>
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  disabled={resolveSpreadsheetUrl(spreadsheetId) === ''}
                  component="a"
                  href={resolveSpreadsheetUrl(spreadsheetId) || undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('importPanel.googleSheet.open')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={importSheet.isPending ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}
                  disabled={!canManage || importSheet.isPending}
                  onClick={runSheetImport}
                  sx={{ minWidth: 232, whiteSpace: 'nowrap' }}
                >
                  {dryRun
                    ? t('importPanel.googleSheet.checkFromSheet')
                    : t('importPanel.googleSheet.importFromSheet')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Custom feed (own format) */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {t('importPanel.feed.heading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('importPanel.feed.description')}
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t('importPanel.feed.mapping.heading')}
                </Typography>
                {mapping.isError && !mappingNotConfigured ? (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {mapping.error?.message ?? t('common:error.unknown')}
                  </Alert>
                ) : null}
                {mappingNotConfigured ? (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {t('importPanel.feed.mapping.notConfigured')}
                  </Alert>
                ) : null}
                <Stack spacing={1.5}>
                  <TextField
                    label={t('importPanel.feed.mapping.sheetNameLabel')}
                    value={mappingSheetName}
                    onChange={(e) => setMappingSheetName(e.target.value)}
                    disabled={!canManage}
                    size="small"
                  />
                  <Stack spacing={1}>
                    {mappingRows.map((row, index) => (
                      <Stack key={index} direction="row" spacing={1} alignItems="center">
                        <TextField
                          label={t('importPanel.feed.mapping.headerLabel')}
                          value={row.header}
                          onChange={(e) => setMappingRow(index, { header: e.target.value })}
                          disabled={!canManage}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label={t('importPanel.feed.mapping.fieldLabel')}
                          value={row.field}
                          onChange={(e) => setMappingRow(index, { field: e.target.value })}
                          disabled={!canManage}
                          size="small"
                          fullWidth
                        />
                        <IconButton
                          aria-label={t('common:action.remove')}
                          onClick={() => removeMappingRow(index)}
                          disabled={!canManage || mappingRows.length <= 1}
                          size="small"
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      startIcon={<AddRoundedIcon />}
                      onClick={addMappingRow}
                      disabled={!canManage}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {t('importPanel.feed.mapping.addRow')}
                    </Button>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mappingActive}
                        onChange={(e) => setMappingActive(e.target.checked)}
                        disabled={!canManage}
                      />
                    }
                    label={t('importPanel.feed.mapping.activeLabel')}
                  />
                  <Button
                    variant="outlined"
                    startIcon={
                      createMapping.isPending || updateMapping.isPending ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SaveRoundedIcon />
                      )
                    }
                    disabled={
                      !canManage ||
                      mappingSheetName.trim() === '' ||
                      createMapping.isPending ||
                      updateMapping.isPending
                    }
                    onClick={saveMapping}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('common:action.save')}
                  </Button>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t('importPanel.feed.upload.heading')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartRoundedIcon />}
                    onClick={() => feedFileInputRef.current?.click()}
                    disabled={!canManage}
                  >
                    {t('importPanel.excel.chooseFile')}
                  </Button>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                    {feedFile?.name ?? t('importPanel.excel.noFileChosen')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={
                      uploadFeed.isPending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CloudUploadRoundedIcon />
                      )
                    }
                    disabled={!canManage || !feedFile || uploadFeed.isPending}
                    onClick={runFeedUpload}
                  >
                    {t('common:action.upload')}
                  </Button>
                </Stack>
                <input
                  ref={feedFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  onChange={(e) => setFeedFile(e.target.files?.[0] ?? null)}
                />
                {feedFileId ? (
                  <Alert
                    severity={
                      feedFileStatus.data?.status === 'FAILED' ||
                      feedFileStatus.data?.status === 'VALIDATION_ERROR'
                        ? 'error'
                        : 'success'
                    }
                    icon={false}
                    sx={{ mt: 1.5 }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {feedFileStatus.data ? (
                          <Chip
                            size="small"
                            color={feedFileStatusColor(feedFileStatus.data.status)}
                            label={feedFileStatus.data.status}
                          />
                        ) : (
                          <CircularProgress size={16} />
                        )}
                        <Typography variant="body2" noWrap>
                          {feedFileStatus.data?.fileName ?? feedFileId}
                        </Typography>
                      </Stack>
                      {feedFileStatus.data ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('importPanel.lastRun.startedAndFinished', {
                            startDate: formatDateTime(feedFileStatus.data.createdAt),
                            finishDate: formatDateTime(feedFileStatus.data.updatedAt),
                          })}
                        </Typography>
                      ) : null}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          reprocessFeed.isPending ? <CircularProgress size={16} /> : <RefreshRoundedIcon />
                        }
                        disabled={!canManage || reprocessFeed.isPending}
                        onClick={runReprocess}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {t('importPanel.feed.upload.reprocess')}
                      </Button>
                    </Stack>
                  </Alert>
                ) : null}
              </Box>

              <Divider />

              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {t('importPanel.feed.export.heading')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <TextField
                    label={t('importPanel.feed.export.articleLabel')}
                    value={exportArticle}
                    onChange={(e) => setExportArticle(e.target.value)}
                    disabled={!canManage}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    disabled={!canManage || exportArticle.trim() === '' || exportProduct.isPending}
                    startIcon={
                      exportProduct.isPending ? <CircularProgress size={16} /> : <PublishRoundedIcon />
                    }
                    onClick={runExportProduct}
                  >
                    {t('importPanel.feed.export.exportProduct')}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!canManage || exportAll.isPending}
                    startIcon={exportAll.isPending ? <CircularProgress size={16} /> : <PublishRoundedIcon />}
                    onClick={runExportAll}
                  >
                    {t('importPanel.feed.export.exportAll')}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Last run */}
        {lastRun ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('importPanel.lastRun.heading')}
              </Typography>
              <RunSummary run={lastRun} />
            </CardContent>
          </Card>
        ) : null}
      </Stack>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
}
